import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useProductosMaterialContext } from 'context';
import { useApi } from 'lib/api';
import { puedeUsarCerrarNomina } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';

export { puedeUsarCerrarNomina };

const btnBase = { textTransform: 'none', borderRadius: 1.8, fontWeight: 700, boxShadow: 'none' };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: '#FFFFFF',
  border: `1px solid ${c}`,
  '&:hover': { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
  '&.Mui-disabled': { color: FP.color.textMuted, borderColor: FP.color.border },
});
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: '#FFFFFF',
  '&:hover': { bgcolor: hover, boxShadow: 'none', color: '#FFFFFF' },
  '&.Mui-disabled': { bgcolor: `${bg}66`, color: '#FFFFFF' },
});

function formatoFecha(valor) {
  const texto = String(valor || '').trim();
  if (!texto || texto.length < 10) return '—';
  const [y, m, d] = texto.slice(0, 10).split('-');
  return d && m && y ? `${d}-${m}-${y}` : texto;
}

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

/**
 * Cerrar nómina activa (fps8030). Pasa pagosnom a historial y cierra el período.
 * El menú y Acciones solo la muestran si el rol tiene `cerrarnomina`.
 */
const CerrarNomina = ({ open = false, onClose, embedded = false }) => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, roleDetalles } = controller;
  const { post } = useApi();

  const puedeUsar = useMemo(() => puedeUsarCerrarNomina(roleDetalles), [roleDetalles]);
  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog);

  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [periodo, setPeriodo] = useState(null);
  const [empleados, setEmpleados] = useState(0);

  const credencialesEmpresa = useCallback(
    () => ({ id_empresa: selectedCompany }),
    [selectedCompany]
  );

  const cargar = useCallback(async () => {
    if (!puedeUsar || !puedeOperar) {
      setPeriodo(null);
      return;
    }
    setCargando(true);
    try {
      const { ok, data } = await post('/api/nomina_obtenercierre', credencialesEmpresa());
      if (!ok) throw new Error(data.Error || 'Error al leer la nómina activa');
      setPeriodo(data.periodo || null);
      setEmpleados(Number(data.empleados) || 0);
    } catch (e) {
      setPeriodo(null);
      showError(e.message || 'Error al leer la nómina activa');
    } finally {
      setCargando(false);
    }
  }, [puedeUsar, puedeOperar, post, credencialesEmpresa, showError]);

  useEffect(() => {
    if ((open || embedded) && puedeUsar && puedeOperar) cargar();
  }, [open, embedded, puedeUsar, puedeOperar, cargar]);

  const cerrar = () => {
    if (procesando) return;
    setConfirmar(false);
    if (onClose) onClose();
  };

  const pedirConfirmacion = () => {
    if (!puedeUsar) {
      showError('No tiene permisos para cerrar la nómina');
      return;
    }
    if (!periodo) {
      showError('No hay una nómina activa para cerrar');
      return;
    }
    setConfirmar(true);
  };

  const procesar = async () => {
    if (!puedeUsar) {
      showError('No tiene permisos para cerrar la nómina');
      return;
    }
    setProcesando(true);
    try {
      const { ok, data } = await post('/api/nomina_cerrar', credencialesEmpresa());
      if (!ok) throw new Error(data.Error || 'No se pudo cerrar la nómina');
      showSuccess(data.ok || 'Nómina cerrada correctamente');
      setConfirmar(false);
      if (onClose) onClose();
      else await cargar();
    } catch (e) {
      showError(e.message || 'No se pudo cerrar la nómina');
    } finally {
      setProcesando(false);
    }
  };

  const cuerpo = !puedeOperar ? (
    <Typography sx={{ color: FP.color.textSecondary, py: 2 }}>
      Seleccione una empresa para cerrar la nómina.
    </Typography>
  ) : cargando ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress size={28} sx={{ color: FP.color.navy }} />
    </Box>
  ) : (
    <Grid container spacing={2} sx={{ mt: 0 }}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Año"
          value={periodo?.ano ?? ''}
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
          sx={fpInputSx}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Número"
          value={periodo?.numero ?? ''}
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
          sx={fpInputSx}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}>
          {periodo
            ? `Período ${formatoFecha(periodo.fecha1)} al ${formatoFecha(periodo.fecha2)} · ${empleados} empleado${empleados === 1 ? '' : 's'} en la nómina activa.`
            : 'No hay una nómina activa para cerrar. Defina e inicie un período primero.'}
        </Typography>
        {periodo ? (
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.82rem', mt: 1 }}>
            El cierre pasa los pagos al historial, actualiza préstamos e incapacidades y deja el
            período cerrado. Esta acción no se puede deshacer.
          </Typography>
        ) : null}
      </Grid>
    </Grid>
  );

  const acciones = (
    <>
      {onClose ? (
        <Button onClick={cerrar} disabled={procesando} sx={btnOutline(FP.color.textSecondary)}>
          Cancelar
        </Button>
      ) : null}
      <Button
        onClick={pedirConfirmacion}
        disabled={procesando || cargando || !puedeUsar || !periodo}
        startIcon={procesando ? <CircularProgress size={16} color="inherit" /> : <LockOutlinedIcon />}
        sx={btnSolid(FP.color.navy, FP.color.navyDeep)}
      >
        {procesando ? 'Procesando…' : 'Procesar'}
      </Button>
    </>
  );

  const dialogoConfirmacion = (
    <Dialog
      open={Boolean(confirmar)}
      onClose={() => !procesando && setConfirmar(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>Cerrar nómina activa</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.9rem' }}>
          ¿Confirma cerrar el año {periodo?.ano} · nómina {periodo?.numero}? Los pagos pasarán al
          historial y el período quedará cerrado.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => setConfirmar(false)}
          disabled={procesando}
          sx={btnOutline(FP.color.textSecondary)}
        >
          Cancelar
        </Button>
        <Button
          onClick={procesar}
          disabled={procesando}
          startIcon={procesando ? <CircularProgress size={16} color="inherit" /> : <LockOutlinedIcon />}
          sx={btnSolid(FP.color.navy, FP.color.navyDeep)}
        >
          {procesando ? 'Cerrando…' : 'Cerrar nómina'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (!puedeUsar) return null;

  if (embedded) {
    return (
      <Box>
        {cuerpo}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>{acciones}</Box>
        {dialogoConfirmacion}
      </Box>
    );
  }

  return (
    <>
      <Dialog
        open={Boolean(open)}
        onClose={cerrar}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Cerrar nómina activa</DialogTitle>
        <DialogContent>{cuerpo}</DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>{acciones}</DialogActions>
      </Dialog>
      {dialogoConfirmacion}
    </>
  );
};

export function CerrarNominaPage() {
  const { controller } = useProductosMaterialContext();
  const { roleDetalles, nombreNegocio } = controller;
  const puedeUsar = useMemo(() => puedeUsarCerrarNomina(roleDetalles), [roleDetalles]);

  if (!puedeUsar) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
        <PageHeader title="Cerrar nómina" subtitle="Nómina" />
        <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <LockOutlinedIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
              No tienes permisos para cerrar la nómina.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Cerrar nómina"
        subtitle={nombreNegocio ? `${nombreNegocio} · Nómina` : 'Nómina'}
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Card sx={cardShellSx}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <CerrarNomina embedded open />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default CerrarNomina;
